
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingSpinner from './LoadingSpinner';
import SectionHeader from './SectionHeader';
import PhoneNumberInput from './PhoneNumberInput';
import TeamMemberEmailField from './TeamMemberEmailField';
import { formatPhoneForDisplay } from '../utils/phoneNumber';
import './TransactionTeam.css';
import { getContractApiUrl } from '../utils/apiUrls';
import useViewerSubscription from '../hooks/useViewerSubscription';
import { canEditTransactionTeam, isProtectedTeamRole } from '../utils/dealActionVisibility';
import { teamMemberEmailPayload } from '../utils/teamMemberEmail';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

const ROLES = [
    { value: 'buyer', label: 'Buyer' },
    { value: 'seller', label: 'Seller' },
    { value: 'sellers_agent', label: "Seller's Agent" },
    { value: 'buyer_agent', label: "Buyer's Agent" },
    { value: 'buyers_referring_agent', label: "Buyer's Referring Agent" },
    { value: 'sellers_referring_agent', label: "Seller's Referring Agent" },
    { value: 'buyers_managing_broker', label: "Buyer's Managing Broker" },
    { value: 'sellers_managing_broker', label: "Seller's Managing Broker" },
    { value: 'title_officer', label: 'Title Officer' },
    { value: 'inspector', label: 'Inspector' },
    { value: 'appraiser', label: 'Appraiser' },
    { value: 'mortgage_broker', label: 'Mortgage Broker' },
    { value: 'lender', label: 'Lender' },
    { value: 'insurance_agent', label: 'Insurance Agent/Broker' },
    { value: 'escrow_officer', label: 'Escrow Officer' },
    { value: 'buyers_transaction_coordinator', label: "Buyer's Transaction Coordinator" },
    { value: 'sellers_transaction_coordinator', label: "Seller's Transaction Coordinator" },
    { value: 'real_estate_attorney', label: 'Real Estate Attorney' },
    { value: 'surveyor', label: 'Surveyor' },
    { value: 'participant', label: 'Participant' }
];

// Ordered list of roles for the Transaction Team column display.
// Members are sorted by this order first, then by insertion order (id) within each role.
const TRANSACTION_TEAM_ROLE_ORDER = [
    'sellers_agent',
    'buyer_agent',
    'sellers_referring_agent',
    'buyers_referring_agent',
    'sellers_managing_broker',
    'buyers_managing_broker',
    'escrow_officer',
    'title_officer',
    'real_estate_attorney',
    'mortgage_broker',
    'lender',
    'insurance_agent',
    'sellers_transaction_coordinator',
    'buyers_transaction_coordinator',
    'inspector',
    'appraiser',
    'surveyor',
    'participant',
];

// Normalise a raw role string from the DB to a canonical ROLES value so the
// sort index lookup works regardless of how the role was stored.
function normaliseRole(role) {
    if (!role) return role;
    // Exact value match
    let match = ROLES.find(r => r.value === role);
    if (match) return match.value;
    // Label match
    match = ROLES.find(r => r.label === role);
    if (match) return match.value;
    // Known string variants (mirrors openEditModal logic)
    if (role === "Buyer's Agent") return 'buyer_agent';
    if (role === "Listing Agent") return 'sellers_agent';
    if (["Transaction Coordinator", "transaction_coordinator", "tc"].includes(role))
        return 'buyers_transaction_coordinator';
    return role;
}

function getTransactionTeamSortIndex(role) {
    const canonical = normaliseRole(role);
    const idx = TRANSACTION_TEAM_ROLE_ORDER.indexOf(canonical);
    return idx === -1 ? TRANSACTION_TEAM_ROLE_ORDER.length : idx;
}

function isPrincipalRole(role) {
    const canonical = (role || '').toLowerCase().replace(/_/g, '-');
    return (
        canonical === 'buyer'
        || canonical === 'seller'
        || canonical === 'co-buyer'
        || canonical === 'co-seller'
    );
}

function principalSide(role) {
    const canonical = (role || '').toLowerCase().replace(/_/g, '-');
    if (canonical === 'buyer' || canonical === 'co-buyer') return 'buyer';
    if (canonical === 'seller' || canonical === 'co-seller') return 'seller';
    return null;
}

function isSamePrincipalSide(roleA, roleB) {
    const sideA = principalSide(roleA);
    const sideB = principalSide(roleB);
    return !!sideA && sideA === sideB;
}

const TransactionTeam = ({
    contractId,
    canManageTeam = false,
    canAssignProtectedTeamRoles = false,
    isExpanded,
    onToggle,
}) => {
    const { user } = useAuth0();
    const { isGuest, loaded: viewerLoaded } = useViewerSubscription();
    const canEditTeam = canEditTransactionTeam({
        viewerLoaded,
        isGuest,
        canManageTeam,
    });
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [error, setError] = useState(null);
    const [canChangeAnyRole, setCanChangeAnyRole] = useState(false);

    const [editingMember, setEditingMember] = useState(null); // If set, we are editing

    // Form State
    const [formData, setFormData] = useState({
        role: 'buyer',
        name: '',
        email: '',
        phone: '',
        company: '',
        send_invite: true,
        is_entity: false,
        signs_as_entity_signee: false,
    });

    useEffect(() => {
        if (contractId && isExpanded) {
            fetchTeam();
        }
    }, [contractId, isExpanded]);

    useEffect(() => {
        if (user?.email && contractId) {
            axios.get(`${BACKEND_URL}/user/profile?email=${encodeURIComponent(user.email)}&contract_id=${contractId}`)
                .then(res => {
                    const { is_super_admin, is_paying_subscriber, is_contract_uploader } = res.data || {};
                    setCanChangeAnyRole(!!(is_super_admin || is_paying_subscriber || is_contract_uploader));
                })
                .catch(() => {});
        }
    }, [user?.email, contractId]);

    const fetchTeam = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${BACKEND_URL}/contracts/${contractId}/team`);
            setTeamMembers(response.data);
        } catch (err) {
            console.error("Error fetching team:", err);
            setError("Failed to load team members.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            if (name === 'is_entity') {
                return {
                    ...prev,
                    is_entity: checked,
                    signs_as_entity_signee: checked ? false : prev.signs_as_entity_signee,
                };
            }
            if (name === 'signs_as_entity_signee') {
                return {
                    ...prev,
                    signs_as_entity_signee: checked,
                    is_entity: checked ? false : prev.is_entity,
                };
            }
            if (name === 'company') {
                return {
                    ...prev,
                    company: value,
                    signs_as_entity_signee: value.trim() ? prev.signs_as_entity_signee : false,
                };
            }
            if (name === 'role') {
                const next = { ...prev, role: value };
                if (!isPrincipalRole(value)) {
                    next.is_entity = false;
                    next.signs_as_entity_signee = false;
                } else if (!isSamePrincipalSide(prev.role, value)) {
                    next.signs_as_entity_signee = false;
                }
                return next;
            }
            return {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
        });
    };

    const openAddModal = () => {
        setEditingMember(null);
        setFormData({
            role: 'buyer',
            name: '',
            email: '',
            phone: '',
            company: '',
            send_invite: true,
            is_entity: false,
            signs_as_entity_signee: false,
        });
        setShowAddModal(true);
    };

    const openEditModal = (member) => {
        setEditingMember(member);

        // Normalize role to match select options
        let roleValue = member.role;

        // 1. Try exact match on value
        let match = ROLES.find(r => r.value === member.role);

        // 2. Try match on label
        if (!match) {
            match = ROLES.find(r => r.label === member.role);
        }

        // 3. Handle specific mismatches (e.g. backend "Buyer's Agent" vs frontend "Buyer Agent")
        if (!match) {
            if (member.role === "Buyer's Agent") match = ROLES.find(r => r.value === 'buyer_agent');
            if (member.role === "Listing Agent") match = ROLES.find(r => r.value === 'sellers_agent');
            if (member.role === "Transaction Coordinator" || member.role === "transaction_coordinator" || member.role === "tc") {
                match = ROLES.find(r => r.value === 'buyers_transaction_coordinator');
            }
        }

        if (match) {
            roleValue = match.value;
        }

        setFormData({
            role: roleValue,
            name: member.name || '',
            email: member.email || '',
            phone: member.phone || '',
            company: member.signs_for_entity_name || member.company || '',
            send_invite: false, // Default to false for edits usually
            is_entity: !!member.is_entity,
            signs_as_entity_signee: !member.is_entity && !!(
                member.signs_for_entity_id || member.signs_for_entity_name
            ),
        });
        setShowAddModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            if (editingMember) {
                // UPDATE
                const isParticipant = editingMember.role?.toLowerCase() === 'participant';
                const roleChanged = formData.role !== editingMember.role;
                const principal = isPrincipalRole(formData.role);
                const canEditEmail = (
                    canAssignProtectedTeamRoles
                    || !isProtectedTeamRole(editingMember.role)
                );
                await axios.put(`${BACKEND_URL}/contracts/${contractId}/team/${editingMember.id}`, {
                    name: formData.name,
                    ...(canEditEmail ? teamMemberEmailPayload(editingMember, formData.email) : {}),
                    phone: formData.phone,
                    company: formData.company,
                    requester_email: user?.email || '',
                    ...(principal ? {
                        is_entity: !!formData.is_entity,
                        signs_as_entity_signee: !!formData.signs_as_entity_signee,
                    } : {}),
                    ...((isParticipant || (canChangeAnyRole && roleChanged)) ? { role: formData.role } : {})
                });
            } else {
                // CREATE
                const principal = isPrincipalRole(formData.role);
                await axios.post(`${BACKEND_URL}/contracts/${contractId}/team`, {
                    ...formData,
                    is_entity: principal && !!formData.is_entity,
                    signs_as_entity_signee: principal && !!formData.signs_as_entity_signee,
                    inviter_name: user?.name || user?.email || 'A KeyFlo User',
                    requester_email: user?.email || '',
                });
            }

            setShowAddModal(false);
            setEditingMember(null);
            fetchTeam(); // Refresh
        } catch (err) {
            console.error("Error saving member:", err);
            alert("Failed to save member: " + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMember = async () => {
        if (!editingMember || !window.confirm("Are you sure you want to remove this team member?")) return;

        try {
            setLoading(true);
            await axios.delete(`${BACKEND_URL}/contracts/${contractId}/team/${editingMember.id}`);
            setShowAddModal(false);
            setEditingMember(null);
            fetchTeam();
        } catch (err) {
            console.error("Error deleting member:", err);
            alert("Failed to delete member: " + (err.message));
        } finally {
            setLoading(false);
        }
    }

    const getRoleLabel = (roleVal) => {
        const role = ROLES.find(r => r.value === roleVal);
        if (role) return role.label;
        if (roleVal === "Transaction Coordinator" || roleVal === "transaction_coordinator" || roleVal === "tc") {
            return "Buyer's Transaction Coordinator";
        }
        return roleVal.replace('_', ' '); // Fallback
    };

    if (!contractId) return null;

    // Grouping for 3-column layout
    const buyers = teamMembers.filter(m =>
        (m.role.toLowerCase().includes('buyer') && !m.role.toLowerCase().includes('agent') && !m.role.toLowerCase().includes('coordinator'))
    );
    const sellers = teamMembers.filter(m =>
        (m.role.toLowerCase().includes('seller') && !m.role.toLowerCase().includes('agent') && !m.role.toLowerCase().includes('coordinator'))
    );
    const others = teamMembers
        .filter(m => !buyers.includes(m) && !sellers.includes(m))
        .sort((a, b) => {
            const roleDiff = getTransactionTeamSortIndex(a.role) - getTransactionTeamSortIndex(b.role);
            if (roleDiff !== 0) return roleDiff;
            return (a.id ?? 0) - (b.id ?? 0); // within same role: insertion order
        });

    // Determine user's side for invite restriction
    const myMember = teamMembers.find(m => m.email?.toLowerCase() === user?.email?.toLowerCase());
    const myRole = myMember?.role?.toLowerCase() || '';
    const isSellersAgent = myRole === 'sellers_agent' || myRole === "seller's agent";
    const isBuyersAgent = myRole === 'buyer_agent' || myRole === "buyer's agent";

    const filteredRoles = ROLES.filter(r => {
        const currentRole = normaliseRole(editingMember?.role);
        if (
            !canAssignProtectedTeamRoles
            && isProtectedTeamRole(r.value)
            && currentRole !== r.value
        ) {
            return false;
        }
        if (isSellersAgent) {
            // Seller's Agent can only invite Sellers and Seller's TCs (plus neutral roles?)
            // The prompt says "only invite Sellers and Seller's TC's". I'll be strict if that's the literal intent.
            // But usually, they might need to add Title, etc. 
            // "Agents can only invite people to their side of the transaction - Seller's Agent can only invite Sellers and Seller's TC's, etc."
            const sideRoles = ['seller', 'sellers_transaction_coordinator'];
            const neutralRoles = ['title_officer', 'inspector', 'appraiser', 'mortgage_broker', 'lender', 'buyers_referring_agent', 'sellers_referring_agent', 'buyers_managing_broker', 'sellers_managing_broker', 'insurance_agent', 'escrow_officer', 'real_estate_attorney', 'surveyor'];
            return sideRoles.includes(r.value) || neutralRoles.includes(r.value);
        }
        if (isBuyersAgent) {
            const sideRoles = ['buyer', 'buyers_transaction_coordinator'];
            const neutralRoles = ['title_officer', 'inspector', 'appraiser', 'mortgage_broker', 'lender', 'buyers_referring_agent', 'sellers_referring_agent', 'buyers_managing_broker', 'sellers_managing_broker', 'insurance_agent', 'escrow_officer', 'real_estate_attorney', 'surveyor'];
            return sideRoles.includes(r.value) || neutralRoles.includes(r.value);
        }
        return true;
    });

    const renderCard = (member, variant = 'standard') => {
        const isBuyerOrSeller = variant === 'minimal';
        const missingInfo = !member.email || !member.name || (!member.company && !isBuyerOrSeller);

        return (
            <div key={member.id} className={`team-member-card ${isBuyerOrSeller ? 'card-minimal' : 'card-branded'}`}>
                {!isBuyerOrSeller && (
                    <div className="card-custom-header">
                        <span className="header-role">{getRoleLabel(member.role)}</span>
                        {canEditTeam && (
                        <button className="edit-icon-btn header-edit" onClick={(e) => { e.stopPropagation(); openEditModal(member); }}>
                            ✎
                        </button>
                        )}
                    </div>
                )}
                <div className="member-info">
                    <div className="member-name-row">
                        <div className="member-name-group">
                            <span className="member-name">{member.name || 'Unknown Name'}</span>
                            {member.is_entity && <span className="member-company">Entity</span>}
                            {!member.is_entity && member.signs_for_entity_name && (
                                <span className="member-company">{`Signing for ${member.signs_for_entity_name}`}</span>
                            )}
                            {member.company && !member.is_entity && !member.signs_for_entity_name && (
                                <span className="member-company">{member.company}</span>
                            )}
                        </div>
                        {isBuyerOrSeller && canEditTeam && (
                            <button className="edit-icon-btn" onClick={(e) => { e.stopPropagation(); openEditModal(member); }}>
                                ✎
                            </button>
                        )}
                    </div>
                    <span className="member-email">{member.email || 'No Email'}</span>
                    {member.phone && <span className="member-phone">{formatPhoneForDisplay(member.phone)}</span>}
                    <div className="status-badges">
                        {member.user_id && (
                            <span className="member-status status-active">Active</span>
                        )}
                        {!member.user_id && member.is_invite_pending && member.email && (
                            <span className="member-status status-pending">Invite Pending</span>
                        )}
                        {missingInfo && (
                            <span className="member-status status-missing" title="Missing email, name, or company">
                                Missing Info
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="transaction-team-container">
            <SectionHeader
                title="Key People"
                isExpanded={isExpanded}
                onToggle={onToggle}
            >
                <div className="section-controls">
                    {canEditTeam && (
                    <button
                        className="add-member-header-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            openAddModal();
                        }}
                        title="Add Team Member"
                    >
                        + Add Member
                    </button>
                    )}
                </div>
            </SectionHeader>

            {isExpanded && (
                <div className="team-content">
                    {loading && <div className="loading">Loading key people...</div>}
                    {error && <div className="error">{error}</div>}

                    <div className="key-people-grid">
                        <div className="people-column column-buyer">
                            <div className="column-card">
                                <h3 className="column-header column-header-navy">{buyers.length > 1 ? 'Buyers' : 'Buyer'}</h3>
                                <div className="column-content">
                                    {buyers.map(m => renderCard(m, 'minimal'))}
                                </div>
                            </div>
                        </div>

                        <div className="people-column column-seller">
                            <div className="column-card">
                                <h3 className="column-header column-header-navy">{sellers.length > 1 ? 'Sellers' : 'Seller'}</h3>
                                <div className="column-content">
                                    {sellers.map(m => renderCard(m, 'minimal'))}
                                </div>
                            </div>
                        </div>

                        <div className="people-column column-others">
                            <div className="column-card">
                                <h3 className="column-header column-header-green">Transaction Team</h3>
                                <div className="column-content grid-others">
                                    {others.map(m => renderCard(m, 'standard'))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</h3>
                            <button className="close-modal-btn" onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Role <span style={{ color: 'red', marginLeft: '4px' }}>*</span></label>
                                <select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    disabled={!!editingMember && editingMember.role?.toLowerCase() !== 'participant' && !canChangeAnyRole}
                                    className={!!editingMember && editingMember.role?.toLowerCase() !== 'participant' && !canChangeAnyRole ? 'disabled-input' : ''}
                                >
                                    {filteredRoles.map(r => (
                                        <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Name <span style={{ color: 'red', marginLeft: '4px' }}>*</span></label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter full name"
                                />
                            </div>
                            <div className="form-group">
                                <label>
                                    Company / Brokerage
                                    {!isPrincipalRole(formData.role) && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                                </label>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    placeholder={isPrincipalRole(formData.role)
                                        ? 'Entity name if this person signs for a company'
                                        : 'Enter company name'}
                                />
                            </div>
                            {isPrincipalRole(formData.role) && (
                                <div className="checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="is_entity"
                                            checked={!!formData.is_entity}
                                            onChange={handleInputChange}
                                        />
                                        This buyer/seller is a legal entity
                                    </label>
                                </div>
                            )}
                            {isPrincipalRole(formData.role) && !!formData.company.trim() && !formData.is_entity && (
                                <div className="checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="signs_as_entity_signee"
                                            checked={!!formData.signs_as_entity_signee}
                                            onChange={handleInputChange}
                                        />
                                        {`Add as signee for the entity (${formData.company.trim()})`}
                                    </label>
                                </div>
                            )}
                            <div className="form-group">
                                <label>Email <span style={{ color: 'red', marginLeft: '4px' }}>*</span></label>
                                <TeamMemberEmailField
                                    member={editingMember}
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    disabled={!!editingMember
                                        && isProtectedTeamRole(editingMember.role)
                                        && !canAssignProtectedTeamRoles}
                                />
                                {!!editingMember
                                    && isProtectedTeamRole(editingMember.role)
                                    && !canAssignProtectedTeamRoles
                                    && (
                                        <small className="form-help-text">
                                            Only the deal owner or an admin can change this role&apos;s email.
                                        </small>
                                    )}
                            </div>
                            <div className="form-group">
                                <label>Phone</label>
                                <PhoneNumberInput
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="e.g. 405-555-1234"
                                />
                            </div>
                            {!editingMember && (
                                <div className="checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="send_invite"
                                            checked={formData.send_invite}
                                            onChange={handleInputChange}
                                        />
                                        Send Invite Email
                                    </label>
                                </div>
                            )}

                            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
                                <div>
                                    {editingMember && (
                                        <button type="button" className="btn-danger" onClick={handleDeleteMember}>
                                            Remove Member
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                                    <button type="submit" className="btn-primary" disabled={loading}>
                                        {loading ? 'Saving...' : (editingMember ? 'Save Changes' : 'Add Member')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransactionTeam;
